import { useEffect, useState, useCallback, FormEvent } from "react";
import { useSearchStore } from "../../store/searchStore";
import { loginRedirectUrl } from "../../config/mapConfig";
import {
  ListMembership,
  getListsForListing,
  addToList,
  removeFromList,
  createList,
} from "../../services/api";
import styles from "./ListModal.module.scss";

/**
 * "Add to list" modal for the React map. Opens for the listing in
 * store.listModalListingId. Lets the member tick/untick existing lists (a
 * listing can be in many at once) and create a new list inline. All writes are
 * server-backed; a 401 (expired session) bounces the visitor to login, exactly
 * like the favorite heart.
 *
 * Each list row is an explicit <button> with a custom check indicator rather
 * than a native <input type=checkbox>: the listing detail/map pages are hosted
 * inside the uSkinned theme, whose global form styling intercepts native
 * checkbox clicks (the change event never fires). A button click is reliable.
 */
export default function ListModal() {
  const listingId = useSearchStore((s) => s.listModalListingId);
  const close = useSearchStore((s) => s.closeListModal);

  const [lists, setLists] = useState<ListMembership[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [newName, setNewName] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  const gotoLogin = useCallback(() => {
    const login = loginRedirectUrl();
    if (login) window.location.href = login;
  }, []);

  const load = useCallback(
    async (id: string) => {
      setLoading(true);
      setError("");
      const res = await getListsForListing(id);
      setLoading(false);
      if (res.status === "unauthenticated") return gotoLogin();
      if (res.status === "error") return setError("Couldn't load your lists. Please try again.");
      setLists(res.lists);
    },
    [gotoLogin]
  );

  // (Re)load whenever a listing's modal opens; reset transient UI on close.
  useEffect(() => {
    if (listingId) {
      setNewName("");
      void load(listingId);
    } else {
      setLists([]);
      setError("");
    }
  }, [listingId, load]);

  // Close on Escape while open.
  useEffect(() => {
    if (!listingId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [listingId, close]);

  if (!listingId) return null;

  const toggle = async (list: ListMembership) => {
    if (busyId !== null) return;
    const adding = !list.inList;
    setBusyId(list.id);
    setError("");
    // Optimistic flip.
    setLists((prev) => prev.map((l) => (l.id === list.id ? { ...l, inList: adding } : l)));

    const res = adding
      ? await addToList(list.id, listingId)
      : await removeFromList(list.id, listingId);
    setBusyId(null);

    if (res.status === "unauthenticated") return gotoLogin();
    if (res.status === "error") {
      // Roll back.
      setLists((prev) => prev.map((l) => (l.id === list.id ? { ...l, inList: !adding } : l)));
      setError("Couldn't update that list. Please try again.");
    }
  };

  const submitCreate = async (e: FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setError("");
    const res = await createList(name, listingId);
    setCreating(false);
    if (res.status === "unauthenticated") return gotoLogin();
    if (res.status === "error") return setError("Couldn't create that list. Please try again.");
    setNewName("");
    // Re-fetch so the new (checked) list appears in order with the rest.
    void load(listingId);
  };

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label="Add to a list"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className={styles.dialog}>
        <button type="button" className={styles.close} aria-label="Close" onClick={close}>
          &times;
        </button>
        <h2 className={styles.title}>Add to a list</h2>
        <p className={styles.sub}>Pick one or more lists, or create a new one.</p>

        {loading ? (
          <p className={styles.muted}>Loading…</p>
        ) : lists.length === 0 ? (
          <p className={styles.muted}>You don't have any lists yet. Create one below.</p>
        ) : (
          <ul className={styles.options}>
            {lists.map((l) => (
              <li key={l.id}>
                <button
                  type="button"
                  className={`${styles.option} ${l.inList ? styles.optionChecked : ""}`}
                  aria-pressed={l.inList}
                  disabled={busyId === l.id}
                  onClick={() => toggle(l)}
                >
                  <span className={styles.box} aria-hidden="true">
                    {l.inList ? "✓" : ""}
                  </span>
                  <span className={styles.optionName}>{l.name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <form className={styles.create} onSubmit={submitCreate}>
          <input
            type="text"
            maxLength={255}
            placeholder="New list name"
            aria-label="New list name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <button type="submit" disabled={creating || !newName.trim()}>
            Create
          </button>
        </form>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
